import React from 'react';
import type { IntakeFormValues } from './_sections/IntakeFields';
import {
  formatNationalPhone,
  isValidEmail,
  suggestEmailCorrection,
  validateIntakeFields,
  type IntakeFieldErrors,
  type IntakeFieldName,
} from './intake-validation';
import { listIntakeErrors } from './intake-errors';
import { useWidgetAnnouncer } from '../WidgetAnnouncerProvider';
import { useWidgetI18n } from '../../i18n/WidgetI18nProvider';
import { useIntakeAnalytics } from './useIntakeAnalytics';
import { submitIntakeForm } from './submit-intake-form';
import { focusIntakeField } from './intake-focus';
import { buildInitialIntakeValues } from './intake-form-initial';

export function useIntakeFormState(params: {
  engageId: string;
  sessionId: string;
  threadId: string;
  onCompleted: () => void;
  captureMclidCookie?: boolean;
  viewerCountry: string | null;
}) {
  const {
    engageId,
    sessionId,
    threadId,
    onCompleted,
    captureMclidCookie = false,
    viewerCountry,
  } = params;
  const [values, setValues] = React.useState(() =>
    buildInitialIntakeValues(viewerCountry)
  );
  const [errors, setErrors] = React.useState<IntakeFieldErrors>({});
  const [emailSuggestion, setEmailSuggestion] = React.useState<string | null>(
    null
  );
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);
  const formRef = React.useRef<HTMLFormElement | null>(null);
  const bannerRef = React.useRef<HTMLDivElement | null>(null);
  // Fields that already showed a validation result: only these are revalidated on blur.
  const validatedRef = React.useRef<Set<IntakeFieldName>>(new Set());
  const { t } = useWidgetI18n();
  const { announce } = useWidgetAnnouncer();
  const analytics = useIntakeAnalytics(sessionId, values.prefillIso);
  const submittingRef = React.useRef(false);
  const valuesRef = React.useRef(values);
  valuesRef.current = values;

  // Fire abandon on unmount (panel close unmounts the card while open is still true).
  React.useEffect(() => {
    return () => {
      analytics.trackAbandoned(valuesRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- abandon on unmount only
  }, []);

  // Same announcer as the chat replies: the widget's live region is spoken by VoiceOver.
  function announceFieldErrors(fieldErrors: IntakeFieldErrors) {
    const messages = listIntakeErrors(fieldErrors);
    if (messages.length === 0) return;
    const details = messages.join('. ');
    announce(
      messages.length === 1
        ? t('intake.errorSummaryOne', { details })
        : t('intake.errorSummaryMany', { count: messages.length, details }),
      // Summary strings and field messages are English in every catalog for now.
      { lang: 'en' }
    );
  }

  function onFieldErrors(fieldErrors: IntakeFieldErrors) {
    // After a failed submit every field has been checked once.
    validatedRef.current = new Set([
      'givenName',
      'familyName',
      'email',
      'phoneCountryIso',
      'phone',
      'currentCountryCode',
    ]);
    announceFieldErrors(fieldErrors);
  }

  // Server error without a known field: focus the banner. Focus is its only
  // channel (the banner is not a live region), so it is read once.
  React.useEffect(() => {
    if (!submitError) return;
    const banner = bannerRef.current;
    banner?.focus();
    banner?.scrollIntoView?.({ block: 'nearest' });
  }, [submitError]);

  function onChange(field: keyof IntakeFormValues, value: string | boolean) {
    // Computed from the ref (not a setState updater) so a blur fired in the
    // same event handler, as the country pickers do, validates the new value.
    const prev = valuesRef.current;
    let nextValues: typeof prev;
    if (field === 'phone' && typeof value === 'string') {
      nextValues = {
        ...prev,
        phone: formatNationalPhone(value, prev.phoneCountryIso),
      };
    } else if (field === 'phoneCountryIso' && typeof value === 'string') {
      if (
        prev.prefillIso &&
        value &&
        value !== prev.prefillIso &&
        prev.phoneCountryIso !== value
      ) {
        analytics.trackDialOverride(prev.prefillIso, value);
      }
      nextValues = {
        ...prev,
        phoneCountryIso: value,
        phone: formatNationalPhone(prev.phone, value),
      };
    } else {
      nextValues = { ...prev, [field]: value };
    }
    valuesRef.current = nextValues;
    setValues(nextValues);
    if (field === 'email') setEmailSuggestion(null);
    if (field === 'noFamilyName' && value === true) {
      analytics.trackNoFamilyNameDeclared(values.currentCountryCode);
    }
    setErrors(prev => {
      const next = { ...prev };
      if (field === 'noFamilyName') delete next.familyName;
      if (field === 'phoneCountryIso') {
        delete next.phoneCountryIso;
        delete next.phone;
      }
      if (field in next) delete next[field as IntakeFieldName];
      return next;
    });
  }

  function onBlurField(field: IntakeFieldName) {
    const values = valuesRef.current;
    const hasValue =
      field === 'phone'
        ? values.phone.replace(/\D/g, '').length >= 8
        : field === 'familyName'
          ? !values.noFamilyName && Boolean(values.familyName.trim())
          : Boolean(String(values[field]).trim());
    if (hasValue) analytics.trackFieldCompleted(field);
    if (field === 'email' && isValidEmail(values.email)) {
      setEmailSuggestion(suggestEmailCorrection(values.email));
    }
    // Only fields already validated by a submit get a result on blur, so an
    // untouched form shows no errors; the error clears once the value is fixed.
    const related: IntakeFieldName[] =
      field === 'phone' || field === 'phoneCountryIso'
        ? ['phoneCountryIso', 'phone']
        : [field];
    if (!related.some(name => validatedRef.current.has(name))) return;
    const { errors: next } = validateIntakeFields(values);
    setErrors(prev => {
      const merged = { ...prev };
      for (const name of related) {
        if (next[name]) merged[name] = next[name];
        else delete merged[name];
      }
      return merged;
    });
  }

  function applyEmailSuggestion() {
    if (!emailSuggestion) return;
    onChange('email', emailSuggestion);
    setEmailSuggestion(null);
    // Suggestion button unmounts — restore focus to the email field (a11y).
    focusIntakeField(formRef.current, 'email');
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submittingRef.current) return;
    setSubmitError(null);
    // Invalid presses show errors without flipping the button to its busy state.
    const { errors: frontErrors, e164Phone } = validateIntakeFields(
      valuesRef.current
    );
    const willRequest = Object.keys(frontErrors).length === 0 && !!e164Phone;
    if (willRequest) {
      submittingRef.current = true;
      setSubmitting(true);
    }
    await submitIntakeForm({
      values: valuesRef.current,
      engageId,
      sessionId,
      threadId,
      captureMclidCookie,
      analytics,
      formRef,
      setErrors,
      onFieldErrors,
      setEmailSuggestion,
      setSubmitError,
      onCompleted,
    });
    submittingRef.current = false;
    setSubmitting(false);
  }

  return {
    values,
    errors,
    emailSuggestion,
    submitError,
    submitting,
    formRef,
    bannerRef,
    onChange,
    onBlurField,
    onSubmit,
    applyEmailSuggestion,
    clearSubmitError: () => {
      setSubmitError(null);
      // The banner unmounts with focus inside it: hand focus to Continue.
      formRef.current
        ?.querySelector<HTMLElement>('button[type="submit"]')
        ?.focus();
    },
  };
}
